import { MimeType } from '@aline/common';
import type { Apollo } from 'src/gql/generated';

import type { SkillAttachmentChanges } from './skill-attachment-editor';
import {
  formatSkillTreeItemId,
  INSTRUCTIONS_TREE_ITEM,
  parseSkillTreeItemId,
} from './skill-tree-ids';
import type { SkillTreeItem } from './skill-tree-ids';
import { getParentPath, joinSkillPath, PENDING_DOCUMENT_COPY_LABEL } from './skill-tree-relocation';
import type { RelocatableSkillTreeItem } from './skill-tree-relocation';

type Attachment = Apollo.AgentSkillSimpleFragment['attachments'][number];

/** The fields of a saved file the tree reads. */
export type SavedSkillAttachment = Pick<Attachment, 'contentType' | 'name' | 'path'> &
  Partial<Pick<Attachment, 'sourceDocumentId'>>;

/** A file of the skill as the tree shows it, pending changes applied. */
export interface SkillDocumentItem {
  /** What the file is. Its label and path never decide that. */
  contentType: string;
  /** The folder the file sits in, '' for the skill root. */
  folder: string;
  /** The tree view id of `item`. */
  id: string;
  item: Exclude<RelocatableSkillTreeItem, { kind: 'folder' }>;
  label: string;
  /**
   * Where the file is (or will be) stored. The server picks the stored path of a new PDF or
   * document copy, so for those this is where the tree shows it.
   */
  path: string;
}

/** A row of the tree view. */
export interface SkillFileTreeNode {
  id: string;
  label: string;
  children?: SkillFileTreeNode[];
}

/** The inline input for a new file or folder, and the folder it creates the item in. */
export interface SkillFileTreeDraft {
  /** The folder the new item goes in, '' for the skill root. */
  folder: string;
  item: Extract<SkillTreeItem, { kind: 'fileDraft' | 'folderDraft' }>;
  label: string;
}

/** Case-insensitive, with numbers compared by value, like a code editor's file tree. */
const NAME_COLLATOR = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/** Every file of the skill, saved or not, with pending renames and removals applied. */
export function getSkillDocumentItems({
  attachments,
  changes,
}: {
  attachments: SavedSkillAttachment[];
  changes: SkillAttachmentChanges;
}): SkillDocumentItem[] {
  const saved = attachments
    .filter(
      file =>
        (!changes.retainedAttachmentPaths || changes.retainedAttachmentPaths.includes(file.path)) &&
        !getIsSavedFileSuperseded(file, changes)
    )
    .map(file => {
      const rename = changes.attachmentRenames?.find(item => item.path === file.path);
      const path = rename?.newPath ?? file.path;
      return createDocumentItem({
        contentType: file.contentType,
        folder: getParentPath(path),
        item: { kind: 'saved', path: file.path },
        label: rename?.name ?? file.name,
        path,
      });
    });
  const pdfs = (changes.pdfAttachments ?? []).map(file =>
    createPendingDocumentItem(file, { id: file.id, kind: 'pdf' }, MimeType.PDF)
  );
  const snapshots = (changes.documentSnapshots ?? []).map(file =>
    createPendingDocumentItem(
      file,
      { documentId: file.documentId, kind: 'snapshot' },
      MimeType.DOCX
    )
  );
  const textFiles = (changes.textFiles ?? []).map(file =>
    createDocumentItem({
      contentType: file.contentType,
      folder: getParentPath(file.path),
      item: { kind: 'text', path: file.path },
      label: file.path.split('/').at(-1) ?? file.path,
      path: file.path,
    })
  );
  return [...saved, ...pdfs, ...snapshots, ...textFiles];
}

/** What the tree row `itemId` shows: SKILL.md is Markdown, a folder or draft row is ''. */
export function getSkillTreeItemContentType(
  documentItems: Pick<SkillDocumentItem, 'contentType' | 'id'>[],
  itemId: string
): string {
  if (itemId === formatSkillTreeItemId(INSTRUCTIONS_TREE_ITEM)) return 'text/markdown';
  return documentItems.find(item => item.id === itemId)?.contentType ?? '';
}

/**
 * The rows of the tree view: SKILL.md first, then each level sorted the way a code editor
 * sorts it, folders before files and names in natural order ("file 2" before "file 10").
 * The order follows the names alone, so a row never moves unless it is renamed or moved to
 * another folder. Folders exist through the files in them, plus the empty ones the author
 * created (`folders`). The inline input for a new item shows in its folder, where the
 * folders or the files of that level begin.
 */
export function buildSkillFileTree({
  documentItems,
  draft,
  folders,
}: {
  documentItems: SkillDocumentItem[];
  draft: SkillFileTreeDraft | null;
  folders: string[];
}): SkillFileTreeNode[] {
  const nodes: SkillFileTreeNode[] = [];
  const addFolder = (path: string): SkillFileTreeNode[] => {
    let children = nodes;
    let currentPath = '';
    for (const part of path.split('/').filter(Boolean)) {
      currentPath = joinSkillPath(currentPath, part);
      const id = formatSkillTreeItemId({ kind: 'folder', path: currentPath });
      let folder = children.find(node => node.id === id);
      if (!folder) {
        folder = { children: [], id, label: part };
        children.push(folder);
      }
      folder.children ??= [];
      children = folder.children;
    }
    return children;
  };
  folders.forEach(addFolder);
  documentItems.forEach(file => addFolder(file.folder).push({ id: file.id, label: file.label }));
  sortSkillTreeNodes(nodes);

  if (draft) {
    const siblings = addFolder(draft.folder);
    const index =
      draft.item.kind === 'folderDraft' ? 0 : siblings.filter(node => node.children).length;
    siblings.splice(index, 0, { id: formatSkillTreeItemId(draft.item), label: draft.label });
  }
  return [{ id: formatSkillTreeItemId(INSTRUCTIONS_TREE_ITEM), label: 'SKILL.md' }, ...nodes];
}

/** The row `id` and every row under it, or nothing when the tree has no such row. */
export function getSkillTreeSubtreeIds(nodes: SkillFileTreeNode[], id: string): string[] {
  for (const node of nodes) {
    if (node.id === id) return [id, ...getSkillTreeDescendantIds(node.children ?? [])];
    const found = node.children && getSkillTreeSubtreeIds(node.children, id);
    if (found?.length) return found;
  }
  return [];
}

/**
 * The path segment a move uses. A document copy still waiting for its name is
 * filed under its document id, so two of them can sit in one folder. The tree
 * still labels each one "Adding document…".
 */
export function getSkillTreeMoveName(
  file: Pick<SkillDocumentItem, 'item' | 'label' | 'path'>
): string {
  if (file.item.kind !== 'snapshot') return file.label;
  const pathName = file.path.split('/').at(-1);
  return pathName === file.item.documentId ? file.item.documentId : file.label;
}

/** Every folder of the tree, as paths in the order the tree shows them. */
export function getSkillTreeFolderPaths(nodes: SkillFileTreeNode[]): string[] {
  return nodes.flatMap(node => {
    const item = parseSkillTreeItemId(node.id);
    if (item?.kind !== 'folder') return [];
    return [item.path, ...getSkillTreeFolderPaths(node.children ?? [])];
  });
}

function getSkillTreeDescendantIds(nodes: SkillFileTreeNode[]): string[] {
  return nodes.flatMap(node => [node.id, ...getSkillTreeDescendantIds(node.children ?? [])]);
}

/**
 * Whether a pending change will write or replace this saved file: a text file at its path, an
 * uploaded PDF (stored as `<id>.pdf`), or a new copy of its source document. The tree shows the
 * pending one. Otherwise the render where a save has landed in the skill, while the editor
 * still holds the changes it sent, would show the file twice.
 */
function getIsSavedFileSuperseded(
  file: SavedSkillAttachment,
  changes: SkillAttachmentChanges
): boolean {
  const path =
    changes.attachmentRenames?.find(rename => rename.path === file.path)?.newPath ?? file.path;
  const fileName = file.path.split('/').at(-1);
  return (
    Boolean(changes.textFiles?.some(text => text.path === path)) ||
    Boolean(changes.pdfAttachments?.some(pdf => `${pdf.id}.pdf` === fileName)) ||
    Boolean(
      file.sourceDocumentId &&
      changes.documentSnapshots?.some(copy => copy.documentId === file.sourceDocumentId)
    )
  );
}

function createDocumentItem(item: Omit<SkillDocumentItem, 'id'>): SkillDocumentItem {
  return { ...item, id: formatSkillTreeItemId(item.item) };
}

/**
 * A PDF or document copy not saved yet, shown in the folder it was added to. A document
 * copy is named by the save, after its document. Until then the row says "Adding
 * document…" and its path uses the document id, so two pending copies can share a folder.
 */
function createPendingDocumentItem(
  file: { folderPath?: string | null; name?: string | null },
  item: Extract<SkillDocumentItem['item'], { kind: 'pdf' | 'snapshot' }>,
  contentType: string
): SkillDocumentItem {
  const folder = file.folderPath ?? '';
  const storedName = file.name?.trim() ?? '';
  const label = storedName || PENDING_DOCUMENT_COPY_LABEL;
  const pathName = storedName || (item.kind === 'snapshot' ? item.documentId : label);
  return createDocumentItem({
    contentType,
    folder,
    item,
    label,
    path: joinSkillPath(folder, pathName),
  });
}

/** Folders before files, then names in natural order, at every level. */
function sortSkillTreeNodes(nodes: SkillFileTreeNode[]): void {
  nodes.sort(
    (left, right) =>
      Number(Boolean(right.children)) - Number(Boolean(left.children)) ||
      NAME_COLLATOR.compare(left.label, right.label) ||
      left.id.localeCompare(right.id)
  );
  nodes.forEach(node => node.children && sortSkillTreeNodes(node.children));
}
